import { useEffect, useState } from 'react';
import { competenciesApi } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Loader2, Award, CheckCircle, XCircle } from 'lucide-react';

export default function CompetenciesPage() {
  const [competencies, setCompetencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCompetency, setEditingCompetency] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    expected_behaviors: '',
    non_display_behaviors: '',
  });

  useEffect(() => {
    loadCompetencies();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactive]);

  const loadCompetencies = async () => {
    try {
      const response = await competenciesApi.getAll(!showInactive);
      setCompetencies(response.data);
    } catch (error) {
      console.error('Failed to load competencies:', error);
      toast.error('Failed to load competencies');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const data = {
        name: formData.name,
        description: formData.description,
        expected_behaviors: formData.expected_behaviors.split('\n').filter(b => b.trim()),
        non_display_behaviors: formData.non_display_behaviors.split('\n').filter(b => b.trim()),
      };

      if (editingCompetency) {
        await competenciesApi.update(editingCompetency.id, data);
        toast.success('Competency updated successfully');
      } else {
        await competenciesApi.create(data);
        toast.success('Competency created successfully');
      }

      setDialogOpen(false);
      resetForm();
      loadCompetencies();
    } catch (error) {
      console.error('Failed to save competency:', error);
      toast.error(error.response?.data?.detail || 'Failed to save competency');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (competency) => {
    setEditingCompetency(competency);
    setFormData({
      name: competency.name,
      description: competency.description,
      expected_behaviors: competency.expected_behaviors.join('\n'),
      non_display_behaviors: competency.non_display_behaviors.join('\n'),
    });
    setDialogOpen(true);
  };

  const handleDelete = async (competencyId) => {
    if (!confirm('Are you sure you want to deactivate this competency?')) return;

    try {
      await competenciesApi.delete(competencyId);
      toast.success('Competency deactivated successfully');
      loadCompetencies();
    } catch (error) {
      console.error('Failed to delete competency:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete competency');
    }
  };

  const handleToggleActive = async (competency) => {
    try {
      await competenciesApi.update(competency.id, { is_active: !competency.is_active });
      toast.success(`Competency ${competency.is_active ? 'deactivated' : 'activated'} successfully`);
      loadCompetencies();
    } catch (error) {
      console.error('Failed to toggle competency:', error);
      toast.error('Failed to update competency');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      expected_behaviors: '',
      non_display_behaviors: '',
    });
    setEditingCompetency(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="competencies-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Competencies Management</h1>
          <p className="text-muted-foreground mt-1">
            Define and manage core competencies for performance evaluation
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              checked={showInactive}
              onCheckedChange={setShowInactive}
              data-testid="show-inactive-toggle"
            />
            <Label className="text-sm">Show Inactive</Label>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button data-testid="add-competency-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Competency
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editingCompetency ? 'Edit Competency' : 'Add New Competency'}</DialogTitle>
                <DialogDescription>
                  Define expected behaviors and non-display indicators
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit}>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="name">Competency Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Communicate Effectively"
                      required
                      data-testid="competency-name-input"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Brief description of this competency"
                      required
                      data-testid="competency-description-input"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="expected_behaviors">
                      Expected Behaviors (one per line)
                    </Label>
                    <Textarea
                      id="expected_behaviors"
                      value={formData.expected_behaviors}
                      onChange={(e) => setFormData({ ...formData, expected_behaviors: e.target.value })}
                      placeholder="Enter each expected behavior on a new line..."
                      rows={5}
                      required
                      data-testid="competency-expected-input"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="non_display_behaviors">
                      Non-Display Behaviors (one per line)
                    </Label>
                    <Textarea
                      id="non_display_behaviors"
                      value={formData.non_display_behaviors}
                      onChange={(e) => setFormData({ ...formData, non_display_behaviors: e.target.value })}
                      placeholder="Enter behaviors that indicate this competency is NOT displayed..."
                      rows={5}
                      required
                      data-testid="competency-nondisplay-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting} data-testid="save-competency-btn">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    {editingCompetency ? 'Update' : 'Create'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Competencies List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5" />
            Core Competencies ({competencies.length})
          </CardTitle>
          <CardDescription>
            Competencies used for performance evaluation
          </CardDescription>
        </CardHeader>
        <CardContent>
          {competencies.length === 0 ? (
            <div className="text-center py-12">
              <Award className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No competencies found</h3>
              <p className="text-muted-foreground">
                Start by adding your first competency
              </p>
            </div>
          ) : (
            <Accordion type="single" collapsible className="space-y-2">
              {competencies.map((competency) => (
                <AccordionItem
                  key={competency.id}
                  value={competency.id}
                  className="border rounded-lg px-4"
                >
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                        competency.is_active ? 'bg-green-100' : 'bg-gray-100'
                      }`}>
                        <Award className={`h-4 w-4 ${
                          competency.is_active ? 'text-green-600' : 'text-gray-400'
                        }`} />
                      </div>
                      <div className="text-left">
                        <p className="font-medium">{competency.name}</p>
                        <p className="text-xs text-muted-foreground">{competency.description}</p>
                      </div>
                      {!competency.is_active && (
                        <Badge variant="outline" className="ml-2">Inactive</Badge>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-4">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-medium mb-3 flex items-center gap-2 text-green-700">
                          <CheckCircle className="h-4 w-4" />
                          Expected Behaviors
                        </h4>
                        <ul className="space-y-2">
                          {competency.expected_behaviors.map((behavior, idx) => (
                            <li key={idx} className="text-sm flex items-start gap-2">
                              <span className="text-green-500 mt-1">•</span>
                              {behavior}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-medium mb-3 flex items-center gap-2 text-red-700">
                          <XCircle className="h-4 w-4" />
                          Non-Display Behaviors
                        </h4>
                        <ul className="space-y-2">
                          {competency.non_display_behaviors.map((behavior, idx) => (
                            <li key={idx} className="text-sm flex items-start gap-2">
                              <span className="text-red-500 mt-1">•</span>
                              {behavior}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleToggleActive(competency)}
                        data-testid={`toggle-competency-${competency.id}`}
                      >
                        {competency.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(competency)}
                        data-testid={`edit-competency-${competency.id}`}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
